// AiUsageStates — the AI-usage pages' page states (pen H-11) and the small
// shared parts: no access, no calls yet, a failed read, loading shapes, an
// outcome badge, and a value that shows its exact form on hover.

export {
    Abbreviated,
    LoadFailed,
    NoAccess,
    NoCalls,
    OutcomeBadge,
    OverviewSkeleton,
    TableSkeleton,
};

import type { AiUsageOutcome } from "@bilda/app/agentCallAdmin";
import { appName } from "@bilda/app/identity";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { outcomeLabel, outcomeTone } from "../../app/aiUsage/format.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import { Tooltip } from "../Tooltip/Tooltip.js";

const NoAccess = () => {
    const { t } = useLingui();
    return (
        <div className="ai-usage__state">
            <span className="ai-usage__state-icon" aria-hidden="true">
                <Icon name="shield" size="md" />
            </span>
            <h1 className="ai-usage__state-title">{t`You don't have access to AI usage`}</h1>
            <p className="ai-usage__state-body">
                {t`Only administrators see AI usage. Ask an administrator if you need it.`}
            </p>
            <a className="ai-usage__state-link" href="/">
                {t`Back to ${appName}`}
            </a>
        </div>
    );
};

const NoCalls = ({ onRates }: { readonly onRates: () => void }) => {
    const { t } = useLingui();
    return (
        <div className="ai-usage__state">
            <span className="ai-usage__state-icon" aria-hidden="true">
                <Icon name="sparkle" size="md" />
            </span>
            <h2 className="ai-usage__state-title">{t`No AI calls yet`}</h2>
            <p className="ai-usage__state-body">
                {t`When teachers start using the assistant, cost, tokens and response times show up here.`}
            </p>
            <button type="button" className="ai-usage__link" onClick={onRates}>
                {t`The rate table is already set up`}
                <Icon name="arrow-right" size="xs" />
            </button>
        </div>
    );
};

const LoadFailed = ({ onRetry }: { readonly onRetry: () => void }) => {
    const { t } = useLingui();
    return (
        <div className="ai-usage__state" role="alert">
            <span className="ai-usage__state-icon" aria-hidden="true">
                <Icon name="warning" size="md" />
            </span>
            <h2 className="ai-usage__state-title">{t`AI usage could not be loaded`}</h2>
            <p className="ai-usage__state-body">
                {t`The server did not answer as expected. Nothing was changed.`}
            </p>
            <Button variant="secondary" onClick={onRetry}>
                {t`Try again`}
            </Button>
        </div>
    );
};

// Same shapes as the loaded overview, so nothing jumps.
const OverviewSkeleton = () => (
    <div className="ai-usage__overview" aria-busy="true">
        <div className="ai-usage__figures">
            {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="ai-usage__figure">
                    <Skeleton variant="text" width="40%" />
                    <Skeleton
                        variant="block"
                        height="var(--space-8)"
                        width="60%"
                    />
                    <Skeleton variant="text" width="80%" />
                </div>
            ))}
        </div>
        <div className="ai-usage__card">
            <Skeleton variant="block" height="var(--height-chart)" />
        </div>
    </div>
);

const TableSkeleton = () => (
    <div className="ai-usage__table-skeleton" aria-busy="true">
        {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="block" height="var(--size-control-lg)" />
        ))}
    </div>
);

// Outcome as a coloured dot + text: never colour alone.
const OutcomeBadge = ({
    outcome,
    toolCalls,
    pill = false,
}: {
    readonly outcome: AiUsageOutcome;
    readonly toolCalls: number;
    readonly pill?: boolean;
}) => {
    const { i18n } = useLingui();
    const tone = outcomeTone(outcome);
    return (
        <span
            className={`ai-usage__outcome ai-usage__outcome--${tone}${pill ? " ai-usage__outcome--pill" : ""}`}
        >
            <span className="ai-usage__dot" aria-hidden="true" />
            {i18n._(outcomeLabel(outcome, toolCalls))}
        </span>
    );
};

// A shortened value; hover shows the exact one.
const Abbreviated = ({
    shown,
    exact,
    children,
}: {
    readonly shown: string;
    readonly exact: string | undefined;
    readonly children?: ReactNode;
}) => {
    const { t } = useLingui();
    if (exact === undefined || exact === shown) return children ?? shown;
    return (
        <Tooltip content={t`exact: ${exact}`}>
            <button type="button" className="ai-usage__abbr">
                {children ?? shown}
            </button>
        </Tooltip>
    );
};
